<template>
  <div class="kyc-form">

    <div class="card">
      <div class="card-content">

        <div class="level">
          <div class="level-left"></div>
          <div class="level-right header__title">
            <vue-typed-js
              :strings="['KYC', 'Know Your Client']"
              :typeSpeed="150"
              :startDelay="1000"
              :backSpeed="100"
              :backDelay="500"
              :loop="true"
              :loopCount="3"
            >
              <span class="typing"></span>
            </vue-typed-js>
          </div>
        </div>

        <div v-if="!formCompleted">

          <b-message type="is-warning" v-if="isSupplementary">
            You're going to create a linked form for <a :href="`/kyc/${documentId}`">{{ jobNumber ? jobNumber : 'this form' }}</a>
          </b-message>

          <form @submit.prevent ref="form">

            <b-field label="WFM Number" horizontal>
              <b-input
                v-focus
                v-model.trim="jobNumber"
                @keyup.native.enter="loginUser"
                placeholder="Enter WFM job number..."
              />
            </b-field>

            <file-manager-select
              label="File Manager"
              :value="fileManagerId"
              :is-horizontal="true"
              @value-changed="fileManagerId = $event"
            />

            <transition name="fade" mode="out-in">
              <b-field
                label="Company Type"
                horizontal
              >
                  <b-select
                    expanded
                    placeholder="Select company type"
                    v-model="companyType"
                    required
                  >
                    <option
                      v-for="type in $store.state.companyTypes"
                      :value="type.name"
                      :key="type.name">
                      {{ type.description }}
                    </option>
                  </b-select>
              </b-field>
            </transition>

            <transition name="fade" mode="out-in">
              <b-field label="Name" horizontal v-if="companyType">
                <b-input type="text" v-model.trim="companyName" required/>
              </b-field>
            </transition>

            <multiple-persons-field
              label="Number of directors"
              label-single="Director"
              placeholder="If N/A input 0"
              :list-persons="directors"
              v-if="companyType && ['individual_type', 'non_hk_law_firm', 'hk_listed', 'trust', 'charity'].indexOf(companyType) === -1"
            />

            <multiple-persons-field
              label="Number of ultimate beneficial individual owners / beneficiaries"
              label-single="Beneficiary"
              :list-persons="beneficiaries"
              v-if="companyType && ['trust', 'charity'].indexOf(companyType) >= 0"
            />

            <multiple-persons-field
              label="Number of settlors"
              label-single="Settlor"
              :list-persons="settlors"
              v-if="companyType && ['trust', 'charity'].indexOf(companyType) >= 0"
            />

            <hr/>

            <transition name="fade" mode="out-in">
              <b-field
                horizontal
                label="Representative Name"
                v-if="companyType && companyType !== 'individual_type'"
              >
                <b-input type="text" required v-model.trim="representativeName"/>
              </b-field>
            </transition>

            <transition name="fade" mode="out-in">
              <b-field
                horizontal
                label="Representative Position"
                v-if="companyType && companyType !== 'individual_type'"
              >
                <b-input type="text" required v-model.trim="representativePosition"/>
              </b-field>
            </transition>

            <transition name="fade" mode="out-in">
              <b-field
                horizontal
                :label="numberShareholdersLabel"
                v-if="companyType && ['individual_type', 'non_hk_law_firm', 'hk_listed'].indexOf(companyType) === -1"
              >
                <b-input type="number" min="0" max="99" required
                  v-model.number="shareholdersNumber"
                  placeholder="If N/A input 0"
                />
              </b-field>
            </transition>

            <div ref="shareholders" class="shareholders" v-if="companyType">
              <shareholders-panel
                v-if="['individual_type', 'non_hk_law_firm', 'hk_listed'].indexOf(companyType) === -1"
                :name="companyName"
                :qty="shareholdersNumber"
                :is-trust="companyType === 'trust' || companyType === 'charity'"
                @shareholders-change="onShareholderChange"
                @update-shareholders-list="updateShareholdersList"
                @remove-panel="onRemovePanel"
              />
            </div>

            <transition name="fade" mode="out-in">
              <div class="level" v-if="companyType">
                <div class="level-left"></div>
                <div class="level-right">
                  <b-field grouped position="is-right">
                    <button type="submit" class="button is-primary"
                      @click="validateForm">Complete</button>
                  </b-field>
                </div>
              </div>
            </transition>
          </form>
        </div>

        <list-documents
          v-if="formCompleted"
          :listData="listData"
          :treeData="treeData"
          :documentId="documentId"
          :needsLinkedForm="needsLinkedForm"
        />
      </div>
    </div>
    <b-loading :active.sync="$store.state.isLoading"/>
  </div>
</template>

<script>
import Vue from 'vue'

// import KycResult from '@/components/KycForm/KycResult'
import FileManagerSelect from '@/components/FileManagerSelect'
import MultiplePersonsField from '@/components/KycForm/MultiplePersonsField'
import ShareholdersPanel from '@/components/KycForm/ShareholdersPanel'
import ListDocuments from '@/components/KycForm/ListDocuments'

export default {
  name: 'KycForm',
  components: {
    MultiplePersonsField,
    FileManagerSelect,
    ShareholdersPanel,
    ListDocuments
  },
  data () {
    return {
      treeData: {},
      listData: { individual: [], company: [] },
      childKey: 'shareholders',
      documentId: null,
      //
      formCompleted: false,
      childPanels: {},
      companyType: null,
      companyName: '',
      // directorsNumber: 1,
      directors: [],
      beneficiaries: [],
      settlors: [],
      shareholders: {},
      representativeName: null,
      representativePosition: null,
      shareholdersNumber: null,
      fileManagerId: null,
      jobNumber: null,
      isSupplementary: false
    }
  },
  created () {
    // console.log(this.$route.params)
    if (this.$route.params.kycId) {
      this.jobNumber = this.$route.params.jobNumber
      this.isSupplementary = true
      this.documentId = this.$route.params.kycId
    }
    // this.getCompanyTypes()
    this.$store.dispatch('getCompanyTypes')
  },
  computed: {
    needsLinkedForm () {
      return this.companyType === 'non_hk_law_firm'
    },
    numberShareholdersLabel () {
      if (['trust', 'charity'].indexOf(this.companyType) >= 0) return 'Number of trustees'
      return 'Number of shareholders'
    }
  },
  watch: {
    companyType () {
      for (const key in this.childPanels) {
        this.deleteShPanel(key)
      }
    }
  },
  methods: {
    updateShareholdersList (parentRowId, shareholders) {
      this.$set(this.shareholders, parentRowId, shareholders)
      // this.rebuildListData()
    },
    onShareholderChange (rowId, shareholder, qty) {
      if (this.childPanels[rowId]) {
        if (shareholder.type === 'individual_shareholder') {
          this.deleteShPanel(rowId)
        } else {
          const panel = this.childPanels[rowId]
          panel.qty = qty
          panel.name = shareholder.name
        }
      } else {
        if (shareholder.type === 'company') {
          this.createShPanel(shareholder.name, qty, rowId)
        }
      }
    },
    createShPanel (name, qty, parentRowId) {
      const ShPanel = Vue.extend(ShareholdersPanel)
      const instance = new ShPanel({
        propsData: { name, qty, parentRowId }
      })
      instance.$on('shareholders-change', this.onShareholderChange)
      instance.$on('update-shareholders-list', this.updateShareholdersList)
      instance.$on('remove-panel', this.deleteShPanel)
      instance.$mount()
      this.$refs.shareholders.appendChild(instance.$el)
      this.childPanels[parentRowId] = instance
    },
    deleteShPanel (rowId) {
      if (this.childPanels.hasOwnProperty(rowId)) {
        this.childPanels[rowId].$destroy()
        this.childPanels[rowId].$el.remove()
        delete this.childPanels[rowId]
      }
    },
    onRemovePanel (parentRowId) {
      this.deleteShPanel(parentRowId)
      if (this.shareholders.hasOwnProperty(parentRowId)) {
        delete this.shareholders[parentRowId]
      }
    },
    validateForm () {
      if (this.$refs.form.checkValidity() && !this.$store.state.isLoading) {
        this.$store.dispatch('setIsLoading', { isLoading: true })
        this.sendDataToServer()
      }
    },
    sendDataToServer () {
      this.rebuildTreeData()
      this.rebuildListData()
      this.axios.post('/kyc/new', {
        name: this.companyName,
        type: this.companyType,
        treeData: this.treeData,
        listData: this.listData,
        jobNumber: this.jobNumber,
        fileManagerId: this.fileManagerId,
        kycId: this.documentId,
        isSupplementary: this.isSupplementary
      })
        .then(response => {
          this.documentId = response.data.result
          this.formCompleted = true
        })
        .catch(error => {
          console.log(error)
        })
        .finally(_ => {
          this.$store.dispatch('setIsLoading', { isLoading: false })
        })
    },
    onCompanyTypeChange () {
      if (this.childPanels.length > 0) {
        this.childPanels.forEach(panel => {
          panel.$destroy()
        })
      }
    },
    rebuildListData () {
      let treeToList = (n, childKey, parentName) => {
        if (n.type && n.name) {
          let type = null
          let role = null
          if (n.type === 'individual_shareholder') {
            type = 'individual'
            role = 'shareholder'
            if (['trust', 'charity'].indexOf(this.companyType) > -1 && parentName === this.companyName) {
              role = 'trustee'
            }
          } else {
            type = n.type === 'individual_type'
              ? 'individual'
              : 'company'
            role = type
          }

          const entityIdx = this.listData[type].findIndex(entity => {
            if (entity.name === n.name) return true
          })
          if (entityIdx > -1) {
            this.listData[type][entityIdx].role.push(role)
            this.listData[type][entityIdx].parent.push(parentName)
          } else {
            this.listData[type].push(
              {
                name: n.name,
                role: [role],
                parent: [parentName],
                sharesNumber: Number(n.sharesNumber),
                sharesPercentage: Number(n.sharesPercentage)
              }
            )
          }
        }

        // const directorsNames = n.directors
        //   ? Object.values(n.directors)
        //     .map(el => { return { name: el, role: 'director' } })
        //   : []
        // if (directorsNames.length > 0) {
        //   this.listData.individual.push(...directorsNames)
        // }
        if (n.directors) {
          this.addEntitiesFromList(n.directors, this.listData.individual, 'director', n.name)
        }
        // n.directors.forEach(directorName => {
        //   const idx = this.listData.individual.findIndex(entity => {
        //     if (entity.name === directorName) return true
        //   })
        //   if (idx > -1) {
        //     this.listData.individual[idx].role.push(role)
        //     this.listData.individual[idx].parent.push(n.name)
        //   } else {
        //     this.listData.individual.push({ name: directorName, role: ['director'], parent: n.name })
        //   }
        // })
        // const directorsNames = this.getObjectsWithRoleFromList(n.directors, 'director', n.name)
        // this.listData.individual.push(...directorsNames)

        if (n.representativeName) {
          this.addEntitiesFromList(
            [n.representativeName],
            this.listData.individual,
            'representative',
            n.name,
            { position: n.representativePosition }
          )

          // this.listData.individual.push({
          //   name: n.representativeName,
          //   position: n.representativePosition,
          //   role: 'representative',
          //   parent: n.name
          // })
        }

        if (n[childKey] && n[childKey].length > 0) {
          n[childKey].forEach(d => treeToList(d, childKey, n.name))
        }
      }

      this.listData = { individual: [], company: [] }
      treeToList(this.treeData, this.childKey, this.name)

      if (this.beneficiaries.length > 0) {
        this.addEntitiesFromList(
          this.beneficiaries,
          this.listData.individual,
          'beneficiary',
          this.companyName
        )
      }

      // const beneficiariesNames = this.getObjectsWithRoleFromList(this.beneficiaries, 'beneficiary', this.companyName)
      // this.listData.individual.push(...beneficiariesNames)

      // const settlorsNames = this.getObjectsWithRoleFromList(this.settlors, 'settlor', this.companyName)
      // this.listData.individual.push(...settlorsNames)
      if (this.settlors.length > 0) {
        this.addEntitiesFromList(
          this.settlors,
          this.listData.individual,
          'settlor',
          this.companyName
        )
      }
    },
    rebuildTreeData () {
      // if (this.loadedTreeData) return this.loadedTreeData
      const treeData = {name: 'Company', type: 'company'}
      if (this.companyType) treeData.type = this.companyType
      if (this.companyName) treeData.name = this.companyName
      if (this.representativeName) treeData.representativeName = this.representativeName
      if (this.representativePosition) treeData.representativePosition = this.representativePosition
      if (this.directors) treeData.directors = this.directors
      if (this.shareholders) {
        for (const key in this.shareholders) {
          if (key === 'undefined') {
            this.$set(treeData, this.childKey, this.shareholders[key])
          } else {
            const parentNode = this.getObject(treeData.shareholders, 'id', key)

            if (parentNode) {
              this.$set(parentNode, this.childKey, this.shareholders[key])
            }
          }
        }
      }
      // return treeData
      this.treeData = treeData
    },
    getObject (theObject, propName, propValue) {
      var result = null
      if (theObject instanceof Array) {
        for (var i = 0; i < theObject.length; i++) {
          result = this.getObject(theObject[i], propName, propValue)
          if (result) {
            break
          }
        }
      } else {
        for (var prop in theObject) {
          // console.log(prop + ': ' + theObject[prop])
          if (prop === propName) {
            if (theObject[prop] === propValue) {
              return theObject
            }
          }
          if (theObject[prop] instanceof Object || theObject[prop] instanceof Array) {
            result = this.getObject(theObject[prop], propName, propValue)
            if (result) {
              break
            }
          }
        }
      }
      return result
    },
    getObjectsWithRoleFromList (list, role, parent) {
      const names = list
        ? Object.values(list)
          .map(el => { return { name: el, role, parent } })
        : []
      return names
    },
    addEntitiesFromList (listEntitiesFrom, listEntitiesTo, role, parentName, additionalProps) {
      listEntitiesFrom.forEach(newEntityName => {
        const idx = listEntitiesTo.findIndex(existingEntity => {
          if (existingEntity.name === newEntityName) return true
        })

        if (idx > -1) {
          listEntitiesTo[idx].role.push(role)
          listEntitiesTo[idx].parent.push(parentName)

          if (additionalProps) {
            for (const propName in additionalProps) {
              listEntitiesTo[idx][propName] = additionalProps[propName]
            }
          }
        } else {
          listEntitiesTo.push({
            name: newEntityName,
            role: [role],
            parent: [parentName],
            ...additionalProps
          })
        }
      })
    }
  }
}
</script>

<style scoped>

.header__title {
  font-size: 1.5em;
}

.shareholders {
  margin-bottom: 2em;
  margin-top: 2em;
}

</style>

<style>
.fade-enter-active, .fade-leave-active {
  transition: opacity .5s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>
